import {
  CSSProperties,
  forwardRef,
  HTMLAttributes,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'next/compat/router'

import clsx from 'clsx'

import type { DataPacket_Kind, Participant, RemoteParticipant } from 'livekit-client'
import { RoomEvent, Track } from 'livekit-client'
import { ParticipantClickEvent, TrackReferenceOrPlaceholder } from '@livekit/components-core'
import { isTrackReference, isTrackReferencePinned } from '@livekit/components-core'

import { ParticipantPlaceholder } from '@/src/assets/images'
import { Call, LockLockedIcon, ScreenShareIcon } from '@/src/assets/icons'

import { ConnectionQualityIndicator } from './ConnectionQualityIndicator'
import { ParticipantName } from './ParticipantName'
import { TrackMutedIndicator } from './TrackMutedIndicator'
import { MicrophoneBlockedIndicator } from './MicrophoneBlockedIndicator'
import { VideoTrack } from './VideoTrack'
import { AudioTrack } from './AudioTrack'

import {
  ParticipantContext,
  TrackRefContext,
  useEnsureTrackRef,
  useFeatureContext,
  useMaybeLayoutContext,
  useMaybeParticipantContext,
  useMaybeRoomContext,
  useMaybeTrackRefContext,
} from '@/src/context'

import { FocusToggle } from '@/src/components/controls/FocusToggle'
import DirectorControl from '@/src/components/controls/DirectorControl'
import { EphemeralReactions } from '@/src/components/controls/EphemeralReactions/EphemeralReactions'

import { useParticipantTile } from '@/src/hooks'
import { useIsEncrypted } from '@/src/hooks/useIsEncrypted'
import { isCodecSwitchPinSuppressed } from '@/src/lib/screenShareCodec'

import { useAppDispatch, useAppSelector } from '@/src/store/hooks'
import {
  addParticipantEphemeralReaction,
  selectParticipantsReactions,
  setParticipantHand,
} from '@/src/store/slices/reactions'
import { selectProject } from '@/src/store/slices/room'
import { selectBranding, selectIsAdmin } from '@/src/store/slices/meetData'

/**
 * The `ParticipantContextIfNeeded` component only creates a `ParticipantContext`
 * if there is no `ParticipantContext` already.
 * @example
 * ```tsx
 * <ParticipantContextIfNeeded participant={trackReference.participant}>
 *  ...
 * </ParticipantContextIfNeeded>
 * ```
 * @public
 */
export function ParticipantContextIfNeeded(
  props: PropsWithChildren<{
    participant?: Participant
  }>,
) {
  const hasContext = !!useMaybeParticipantContext()
  return props.participant && !hasContext ? (
    <ParticipantContext.Provider value={props.participant}>
      {props.children}
    </ParticipantContext.Provider>
  ) : (
    props.children
  )
}

/**
 * Only create a `TrackRefContext` if there is no `TrackRefContext` already.
 */
export function TrackRefContextIfNeeded(
  props: PropsWithChildren<{
    trackRef?: TrackReferenceOrPlaceholder
  }>,
) {
  const hasContext = !!useMaybeTrackRefContext()
  return props.trackRef && !hasContext ? (
    <TrackRefContext.Provider value={props.trackRef}>{props.children}</TrackRefContext.Provider>
  ) : (
    props.children
  )
}

/** @public */
export interface ParticipantTileProps extends HTMLAttributes<HTMLDivElement> {
  /** The track reference to display. */
  trackRef?: TrackReferenceOrPlaceholder
  disableSpeakingIndicator?: boolean
  onParticipantClick?: (event: ParticipantClickEvent) => void
  isPiP?: boolean
}

const iconStyles: CSSProperties = { marginRight: '0.25rem' }

/**
 * The `ParticipantTile` component is the base utility wrapper for displaying a visual representation of a participant.
 * This component can be used as a child of the `TrackLoop` component or by passing a track reference as property.
 *
 * @example Using the `ParticipantTile` component with a track reference:
 * ```tsx
 * <ParticipantTile trackRef={trackRef} />
 * ```
 * @example Using the `ParticipantTile` component as a child of the `TrackLoop` component:
 * ```tsx
 * <TrackLoop>
 *  <ParticipantTile />
 * </TrackLoop>
 * ```
 * @public
 */
export const ParticipantTile = /* @__PURE__ */ forwardRef<HTMLDivElement, ParticipantTileProps>(
  function ParticipantTile(
    {
      trackRef,
      children,
      onParticipantClick,
      disableSpeakingIndicator,
      isPiP,
      ...htmlProps
    }: ParticipantTileProps,
    ref,
  ) {
    const { t } = useTranslation()
    const trackReference = useEnsureTrackRef(trackRef)
    const room = useMaybeRoomContext()

    const participantsReactions = useAppSelector(selectParticipantsReactions)
    const project = useAppSelector(selectProject)
    const isAdmin = useAppSelector(selectIsAdmin)
    const branding = useAppSelector(selectBranding)

    
    const dispatch = useAppDispatch()

    const { elementProps } = useParticipantTile<HTMLDivElement>({
      htmlProps,
      disableSpeakingIndicator,
      onParticipantClick,
      trackRef: trackReference,
    })
    const router = useRouter()
    const [hiddenNameScreenShare, setHiddenNameScreenShare] = useState<boolean>(false)
    const [isFocusLockActive, setIsFocusLockActive] = useState<boolean>(false)
    const isEncrypted = useIsEncrypted(trackReference.participant)
    const [avatarError, setAvatarError] = useState<boolean>(false)
    const layoutContext = useMaybeLayoutContext()
    const participantIdentity = trackReference.participant.identity || ''
    const isAnimatedFedor =
      participantIdentity.startsWith('avatar-') || participantIdentity.startsWith('fedor-page-')

    useEffect(() => {
      if (!room) return

      const handleMetadataChanged = (metadata: string) => {
        try {
          const parsed = JSON.parse(metadata)
          setIsFocusLockActive(parsed.focusLockEnabled === true)
        } catch (e) {
          setIsFocusLockActive(false)
        }
      }

      if (room.metadata) {
        handleMetadataChanged(room.metadata)
      }

      room.on(RoomEvent.RoomMetadataChanged, handleMetadataChanged)

      return () => {
        room.off(RoomEvent.RoomMetadataChanged, handleMetadataChanged)
      }
    }, [room])

    const autoManageSubscription = useFeatureContext()?.autoSubscription

    const handleSubscribe = useCallback(
      (subscribed: boolean) => {
        // Отписка от старой публикации при смене кодека временная:
        // сохраняем фокус до переноса pin на новую демонстрацию.
        if (
          trackReference.source === Track.Source.ScreenShare &&
          isCodecSwitchPinSuppressed(trackReference.participant.identity)
        ) {
          return
        }

        if (
          trackReference.source &&
          !subscribed &&
          layoutContext &&
          layoutContext.pin.dispatch &&
          isTrackReferencePinned(trackReference, layoutContext.pin.state)
        ) {
          layoutContext.pin.dispatch({ msg: 'clear_pin' })
        }
      },
      [trackReference, layoutContext],
    )

    useEffect(() => {
      if (router?.asPath.includes('director')) {
        setHiddenNameScreenShare(true)
      }
    }, [router])

    const trackMutedIndicatorRef = useMemo(
      () => ({
        participant: trackReference.participant,
        source: Track.Source.Microphone,
      }),
      [trackReference.participant],
    )

    useEffect(() => {
      if (!room) return

      const handleDataReceived = (
        payload: Uint8Array,
        participant?: RemoteParticipant,
        kind?: DataPacket_Kind,
        topic?: string,
      ) => {
        if (topic?.includes('reaction-topic')) {
          try {
            const decoder = new TextDecoder('utf-8')
            const data = JSON.parse(decoder.decode(payload) ?? '{}')

            if (!data || !participant) return

            const identity = participant.identity

            /**
             * Если пришли данные о поднятии руки
             */
            if (data.type === 'raiseHand' && typeof data.isHandRaised === 'boolean') {
              dispatch(
                setParticipantHand({
                  identity,
                  isHandRaised: data.isHandRaised,
                }),
              )
            }

            /**
             * Если пришли данные об "эфемерной" реакции
             */
            if (data.type === 'ephemeral') {
              dispatch(
                addParticipantEphemeralReaction({
                  identity,
                  ephemeral: {
                    id: data.reactionId,
                    type: data.reaction,
                  },
                }),
              )
            }
          } catch (e) {
            console.error('Error with parsing reaction: \n', e)
          }
        }
      }

      room?.on(RoomEvent.DataReceived, handleDataReceived)

      return () => {
        room?.off(RoomEvent.DataReceived, handleDataReceived)
      }
    }, [room])

    return (
      <div ref={ref} style={{ position: 'relative' }} {...elementProps}>
        <TrackRefContextIfNeeded trackRef={trackReference}>
          <ParticipantContextIfNeeded participant={trackReference.participant}>
            {children ?? (
              <>
                {isTrackReference(trackReference) &&
                (trackReference.publication?.kind === 'video' ||
                  trackReference.source === Track.Source.Camera ||
                  trackReference.source === Track.Source.ScreenShare) ? (
                  <VideoTrack
                    trackRef={trackReference}
                    onSubscriptionStatusChanged={handleSubscribe}
                    manageSubscription={autoManageSubscription}
                    isPiP={isPiP}
                  />
                ) : (
                  isTrackReference(trackReference) && (
                    <AudioTrack
                      trackRef={trackReference}
                      onSubscriptionStatusChanged={handleSubscribe}
                    />
                  )
                )}

                <div
                  className={clsx('lk-participant-placeholder', {
                    'fedor-participant-placeholder': trackReference.participant?.permissions?.agent,
                  })}
                  style={{
                    backgroundColor:
                      trackReference.participant?.permissions?.agent && branding?.color_avatar
                        ? branding.color_avatar
                        : undefined,
                  }}
                >
                  {trackReference.participant?.name?.includes(t('sip.participantName')) ? (
                    <Call />
                  ) : trackReference.participant?.permissions?.agent && branding?.image_avatar && !avatarError ? (
                    <img
                      src={branding.image_avatar}
                      alt="Agent Avatar"
                      onError={() => setAvatarError(true)}
                    />
                  ) : (
                    <ParticipantPlaceholder />
                  )}
                </div>

                <div className="lk-participant-metadata">
                  <div className="lk-participant-metadata-item">
                    {trackReference.source === Track.Source.Camera ? (
                      <>
                        {isEncrypted && <LockLockedIcon style={iconStyles} />}
                        {!isAnimatedFedor && (
                          <TrackMutedIndicator trackRef={trackMutedIndicatorRef} show="muted" />
                        )}
                        <MicrophoneBlockedIndicator trackRef={trackReference} />

                        <ParticipantName room={room} />
                      </>
                    ) : (
                      <div style={{ display: hiddenNameScreenShare ? 'none' : 'flex' }}>
                        <ScreenShareIcon style={iconStyles} />
                        <ParticipantName> {t('screen')}</ParticipantName>
                      </div>
                    )}
                  </div>

                  {!isAnimatedFedor && (
                    <ConnectionQualityIndicator className="lk-participant-metadata-item" />
                  )}
                </div>
              </>
            )}

            {!isPiP && (!isFocusLockActive || isAdmin) && <FocusToggle trackRef={trackReference} />}
          </ParticipantContextIfNeeded>

          <DirectorControl trackReference={trackReference} />
        </TrackRefContextIfNeeded>

        {participantsReactions[trackReference?.participant?.identity]?.isHandRaised && (
          <div className="participant-tile-reaction">🖐️</div>
        )}

        <EphemeralReactions participantIdentity={trackReference?.participant?.identity} />
      </div>
    )
  },
)
